import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Pencil, Trash2, ChevronDown, ChevronUp, Pill, Clock, Package } from 'lucide-react'
import type { Medication } from '@/types/medicamento'

const FORMA_LABELS: Record<string, string> = {
  comprimido: 'Comprimido',
  liquido: 'Líquido',
  inalatorio_spray: 'Inalatório / Spray',
  aplicacao: 'Aplicação',
  triturado_diluido: 'Triturado / Diluído'
}

const UNIDADE_LABELS: Record<string, string> = {
  inteiro: 'comprimido(s)',
  fracionado_1_2: 'comprimido(s) 1/2',
  fracionado_1_4: 'comprimido(s) 1/4',
  gotas: 'gotas',
  ml: 'mL',
  jatos: 'jatos',
  ampola: 'ampola(s)'
}

interface MedicamentoItemProps {
  medicamento: Medication
  index: number
  onEdit: (med: Medication) => void
  onRemove: (index: number) => void
}

export function MedicamentoItem({ medicamento, index, onEdit, onRemove }: MedicamentoItemProps) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="border rounded-lg bg-muted/20">
      <div
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 p-3 cursor-pointer hover:bg-muted/40 transition-colors"
      >
        <Pill className="h-4 w-4 text-primary flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{medicamento.name}</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {(medicamento.schedules || []).map((t, i) => (
              <Badge key={i} variant="secondary" className="text-xs gap-1">
                <Clock className="h-3 w-3" /> {t}
              </Badge>
            ))}
          </div>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </div>

      {expanded && (
        <div className="px-3 pb-3 pt-0 space-y-2 text-sm">
          {medicamento.instructions && (
            <p className="text-muted-foreground"><span className="font-medium">Instruções:</span> {medicamento.instructions}</p>
          )}

          {medicamento.medicationForm && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Package className="h-3.5 w-3.5" />
              <span className="font-medium">Forma:</span> {FORMA_LABELS[medicamento.medicationForm]}
              {medicamento.medicationForm === 'triturado_diluido' && medicamento.doseTotalMg && medicamento.dilutionVolumeMl && medicamento.quantityPerDose && (
                <span>
                  — {medicamento.quantityPerDose} mg/dose ({medicamento.doseTotalMg}mg/{medicamento.dilutionVolumeMl}mL)
                  {medicamento.reuseMode === 'descartar' ? ' — 1 comprimido/dose' : ' — reaproveitar'}
                  {medicamento.reuseMode === 'reaproveitar' && (
                    <span className="text-blue-600 font-medium"> ({Math.floor(medicamento.doseTotalMg / medicamento.quantityPerDose)} doses/comprimido)</span>
                  )}
                </span>
              )}
              {medicamento.medicationForm !== 'triturado_diluido' && medicamento.measurementUnit && (
                <span>— {medicamento.quantityPerDose} {UNIDADE_LABELS[medicamento.measurementUnit]} por dose</span>
              )}
            </div>
          )}

          {medicamento.recurrence !== 'daily' && (
            <div className="text-muted-foreground">
              <span className="font-medium">Recorrência:</span>{' '}
              {medicamento.recurrence === 'once' ? 'Apenas uma vez' : 'Período específico'}
              {medicamento.recurrence === 'range' && medicamento.recurrenceStart && (
                <span> ({medicamento.recurrenceStart} a {medicamento.recurrenceEnd})</span>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={(e) => { e.stopPropagation(); onEdit(medicamento); }}>
              <Pencil className="h-3 w-3" /> Editar
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-destructive" onClick={(e) => { e.stopPropagation(); onRemove(index); }}>
              <Trash2 className="h-3 w-3" /> Remover
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
